import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const emptyForm = {
  cn_no: "",
  date: new Date().toISOString().slice(0, 10),
  from_location: "", to_location: "",
  consignor_name: "", consignor_address: "", consignor_gstin: "", consignor_pan: "",
  consignee_name: "", consignee_address: "", consignee_gstin: "",
  inv_no: "", inv_date: "", challan_no: "", eway_bill_no: "", eway_bill_valid_till: "",
  no_pkgs: "", method_of_pkg: "", contents: "",
  actual_weight: "", charged_weight: "", value_declared: "",
  truck_no: "",
  per_kg: "", hamail: "", dt_charge: "", ins_other_charge: "", total: "", gstin_amount: "", grand_total: "",
  freight_type: "To Be Billed",
  remarks: "", seal_no: "",
  door_delivery: false, driver_name: "", driver_phone: "",
};

function num(v) { const n = parseFloat(v); return isNaN(n) ? 0 : n; }

export default function ConsignmentForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [form, setForm] = useState(emptyForm);
  const [parties, setParties] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/parties").then(({data})=>setParties(data)).catch(()=>{});
    api.get("/trucks").then(({data})=>setTrucks(data)).catch(()=>{});
    if (isEdit) {
      api.get(`/consignments/${id}`).then(({data})=> setForm({...emptyForm, ...data})).catch((e)=>toast.error(formatApiError(e.response?.data?.detail)));
    } else {
      api.get("/consignments/next-number").then(({data}) => setForm((f)=>({ ...f, cn_no: data.cn_no }))).catch(()=>{});
    }
  }, [id, isEdit]);

  // auto total = per_kg*charged_weight + hamail + dt + ins  (simple aggregation)
  useEffect(() => {
    const total = num(form.per_kg) * num(form.charged_weight) + num(form.hamail) + num(form.dt_charge) + num(form.ins_other_charge);
    const gst = num(form.gstin_amount);
    const grand = total + gst;
    setForm((f) => ({
      ...f,
      total: total ? total.toFixed(2) : f.total,
      grand_total: (total || gst) ? grand.toFixed(2) : f.grand_total,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.per_kg, form.charged_weight, form.hamail, form.dt_charge, form.ins_other_charge, form.gstin_amount]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const pickConsignor = (name) => {
    const p = parties.find((x) => x.name === name);
    setForm((f) => ({ ...f, consignor_name: name, consignor_address: p?.address ?? f.consignor_address, consignor_gstin: p?.gstin ?? f.consignor_gstin }));
  };
  const pickConsignee = (name) => {
    const p = parties.find((x) => x.name === name);
    setForm((f) => ({ ...f, consignee_name: name, consignee_address: p?.address ?? f.consignee_address, consignee_gstin: p?.gstin ?? f.consignee_gstin }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let res;
      if (isEdit) res = await api.put(`/consignments/${id}`, form);
      else res = await api.post(`/consignments`, form);
      toast.success(isEdit ? "Updated" : `Created ${res.data.cn_no}`);
      navigate(`/portal/consignments/${res.data.id}/print`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="p-8" data-testid="consignment-form-page">
      <h1 className="font-display text-3xl text-slate-900 mb-1">{isEdit ? "Edit Consignment" : "New Consignment Note"}</h1>
      <p className="text-slate-500 text-sm mb-6">C/N No is auto-filled with the next number — you can edit it before saving.</p>

      <form onSubmit={onSubmit} className="cn-form-lg grid grid-cols-1 lg:grid-cols-3 gap-6">
        <section className="bg-white p-5 rounded-xl border border-slate-100 lg:col-span-2 space-y-4">
          <h3 className="font-semibold">Basics</h3>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>C/N Number</Label><Input value={form.cn_no} onChange={set("cn_no")} className="font-bold text-[#0b3a7a]" data-testid="cn-number-input"/></div>
            <div><Label>Date</Label><Input type="date" value={form.date} onChange={set("date")} required data-testid="cn-date"/></div>
            <div><Label>Truck No.</Label>
              <Input list="trucks" value={form.truck_no} onChange={set("truck_no")} placeholder="WB 25 A 1234" data-testid="cn-truck"/>
              <datalist id="trucks">{trucks.map(t => <option key={t.id} value={t.number}/>)}</datalist>
            </div>
            <div><Label>From</Label><Input value={form.from_location} onChange={set("from_location")} data-testid="cn-from"/></div>
            <div><Label>To</Label><Input value={form.to_location} onChange={set("to_location")} data-testid="cn-to"/></div>
            <div><Label>Invoice No.</Label><Input value={form.inv_no} onChange={set("inv_no")}/></div>
            <div><Label>Invoice Date</Label><Input type="date" value={form.inv_date} onChange={set("inv_date")}/></div>
            <div><Label>E-Way Bill No.</Label><Input value={form.eway_bill_no} onChange={set("eway_bill_no")}/></div>
            <div><Label>E-Way Valid Till</Label><Input type="date" value={form.eway_bill_valid_till} onChange={set("eway_bill_valid_till")}/></div>
            <div><Label>Seal No.</Label><Input value={form.seal_no} onChange={set("seal_no")}/></div>
            <div><Label>Driver Name</Label><Input value={form.driver_name} onChange={set("driver_name")} placeholder="e.g. KARAN TANDHAN"/></div>
            <div><Label>Driver Phone</Label><Input value={form.driver_phone} onChange={set("driver_phone")} placeholder="e.g. 7980868556"/></div>
            <div className="flex items-center gap-2 pt-6">
              <input type="checkbox" id="door_delivery" checked={form.door_delivery} onChange={(e)=>setForm(f=>({...f, door_delivery: e.target.checked}))} data-testid="cn-door-delivery"/>
              <Label htmlFor="door_delivery" className="cursor-pointer">Door Delivery C.C. Attached</Label>
            </div>
          </div>

          <h3 className="font-semibold pt-2">Consignor</h3>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Name</Label>
              <Input list="parties1" value={form.consignor_name} onChange={(e)=>pickConsignor(e.target.value)} data-testid="cn-consignor-name"/>
              <datalist id="parties1">{parties.map(p => <option key={p.id} value={p.name}/>)}</datalist>
            </div>
            <div><Label>GSTIN</Label><Input value={form.consignor_gstin} onChange={set("consignor_gstin")}/></div>
            <div><Label>PAN</Label><Input value={form.consignor_pan} onChange={set("consignor_pan")} data-testid="cn-consignor-pan-input"/></div>
            <div className="col-span-2"><Label>Address</Label><Textarea value={form.consignor_address} onChange={set("consignor_address")} rows={2}/></div>
          </div>

          <h3 className="font-semibold pt-2">Consignee</h3>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Name</Label>
              <Input list="parties2" value={form.consignee_name} onChange={(e)=>pickConsignee(e.target.value)} data-testid="cn-consignee-name"/>
              <datalist id="parties2">{parties.map(p => <option key={p.id} value={p.name}/>)}</datalist>
            </div>
            <div><Label>GSTIN</Label><Input value={form.consignee_gstin} onChange={set("consignee_gstin")}/></div>
            <div className="col-span-2"><Label>Address</Label><Textarea value={form.consignee_address} onChange={set("consignee_address")} rows={2}/></div>
          </div>

          <h3 className="font-semibold pt-2">Goods</h3>
          <div className="grid grid-cols-3 gap-4">
            <div><Label>No. of Pkgs</Label><Input value={form.no_pkgs} onChange={set("no_pkgs")} className="border-slate-300 bg-slate-50/60 print:border-none" data-testid="cn-no-pkgs"/></div>
            <div><Label>Method of Pkg</Label><Input value={form.method_of_pkg} onChange={set("method_of_pkg")} className="border-slate-300 bg-slate-50/60 print:border-none" data-testid="cn-method-pkg"/></div>
            <div><Label>Value Declared</Label><Input value={form.value_declared} onChange={set("value_declared")}/></div>
            <div className="col-span-3"><Label>Contents (Said to Contain)</Label><Textarea value={form.contents} onChange={set("contents")} rows={2} className="border-slate-300 bg-slate-50/60 print:border-none" data-testid="cn-contents"/></div>
            <div><Label>Actual Weight (Kg)</Label><Input value={form.actual_weight} onChange={set("actual_weight")}/></div>
            <div><Label>Charged Weight (Kg)</Label><Input value={form.charged_weight} onChange={set("charged_weight")}/></div>
          </div>

          <div className="col-span-2">
            <Label>Remarks</Label><Textarea value={form.remarks} onChange={set("remarks")} rows={2}/>
          </div>
        </section>

        <section className="bg-white p-5 rounded-xl border border-slate-100 space-y-4 h-fit sticky top-4">
          <h3 className="font-semibold">Freight Charges</h3>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Per Kg (₹)</Label><Input value={form.per_kg} onChange={set("per_kg")} inputMode="decimal"/></div>
            <div><Label>Hamail (₹)</Label><Input value={form.hamail} onChange={set("hamail")} inputMode="decimal"/></div>
            <div><Label>DT Charge (₹)</Label><Input value={form.dt_charge} onChange={set("dt_charge")} inputMode="decimal"/></div>
            <div><Label>Ins/Other (₹)</Label><Input value={form.ins_other_charge} onChange={set("ins_other_charge")} inputMode="decimal"/></div>
            <div><Label>Total (₹)</Label><Input value={form.total} onChange={set("total")} readOnly/></div>
            <div><Label>GST Amount (₹)</Label><Input value={form.gstin_amount} onChange={set("gstin_amount")} inputMode="decimal"/></div>
            <div className="col-span-2"><Label>Grand Total (₹)</Label><Input value={form.grand_total} onChange={set("grand_total")} className="font-semibold" data-testid="cn-grand-total"/></div>
          </div>

          <div>
            <Label>Freight Type</Label>
            <Select value={form.freight_type} onValueChange={(v)=>setForm(f=>({...f, freight_type: v}))}>
              <SelectTrigger data-testid="cn-freight-type"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="To Pay">To Pay</SelectItem>
                <SelectItem value="Paid">Paid</SelectItem>
                <SelectItem value="To Be Billed">To Be Billed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button type="submit" disabled={saving} className="w-full btn-primary" data-testid="cn-save-btn">
            {saving ? "Saving…" : (isEdit ? "Save Changes" : "Save & Print")}
          </Button>
        </section>
      </form>
    </div>
  );
}
